import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { getApiUrl as resolveApiUrl } from "@/lib/runtimeConfig";
import { formatApiErrorDetail } from "@/lib/api";
import { AUTH_BUTTON_CLS, AUTH_INPUT_CLS, AuthError, AuthShell, BackToLogin, PASSWORD_HINT } from "@/components/auth/AuthShell";

const API = resolveApiUrl();

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();

    const [preview, setPreview] = useState(null); // {email_masked, expires_at}
    const [error, setError] = useState(null);
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        if (!token) { setError("This reset link is missing its token."); return; }
        let alive = true;
        axios.get(`${API}/auth/reset-password/preview`, { params: { token } })
            .then((r) => { if (alive) setPreview(r.data); })
            .catch((e) => { if (alive) setError(formatApiErrorDetail(e?.response?.data?.detail) || "This reset link is no longer valid."); });
        return () => { alive = false; };
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setFormError(null);
        if (pwd !== pwd2) { setFormError("Passwords don't match"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
            toast.success("Password updated — sign in with your new password");
            navigate("/login", { replace: true });
        } catch (e2) {
            const status = e2?.response?.status;
            const msg = formatApiErrorDetail(e2?.response?.data?.detail) || "Could not reset the password.";
            if (status === 410) setError(msg); else setFormError(msg);
            setBusy(false);
        }
    };

    return (
        <AuthShell kicker="Password reset" title={error ? null : "Choose a new password"} testId="reset-password-page">
            {error ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4" data-testid="reset-password-invalid">
                    <div className="text-sm text-red-300">{error}</div>
                    <div className="mt-3 flex items-center gap-4">
                        <button type="button" onClick={() => navigate("/forgot-password")} className="text-xs text-[#a17bff] hover:text-[#c7a6ff]" data-testid="reset-password-request-new">
                            Request a new link
                        </button>
                        <BackToLogin />
                    </div>
                </div>
            ) : !preview ? (
                <div className="text-sm text-white/50 py-12 text-center" data-testid="reset-password-validating">Validating link…</div>
            ) : (
                <>
                    <div className="bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-md p-4 mb-5" data-testid="reset-password-preview">
                        <div className="flex items-center gap-2 text-xs text-[#a17bff] mb-1">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            <span className="uppercase tracking-wider">Link valid</span>
                        </div>
                        <div className="text-sm">
                            Resetting the password for <strong data-testid="reset-password-email">{preview.email_masked}</strong>.
                        </div>
                        <div className="text-xs text-white/60 mt-1">All your existing sessions will be signed out.</div>
                    </div>
                    <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
                        <label className="block">
                            <div className="text-xs text-white/50 mb-1.5">New password</div>
                            <div className="relative">
                                <input
                                    type={show ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)}
                                    autoFocus required minLength={12} maxLength={200} autoComplete="new-password"
                                    className={`${AUTH_INPUT_CLS} pr-10`} placeholder="≥ 12 characters" data-testid="reset-password-input"
                                />
                                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-2 text-white/40 hover:text-white/70 p-1"
                                    aria-label={show ? "hide password" : "show password"} data-testid="reset-password-toggle">
                                    {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                            </div>
                            <div className="text-[11px] text-white/40 mt-1">{PASSWORD_HINT}</div>
                        </label>
                        <label className="block">
                            <div className="text-xs text-white/50 mb-1.5">Confirm password</div>
                            <input
                                type={show ? "text" : "password"} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                                required minLength={12} maxLength={200} autoComplete="new-password"
                                className={AUTH_INPUT_CLS} data-testid="reset-password-confirm-input"
                            />
                        </label>
                        <AuthError message={formError} testId="reset-password-error" />
                        <button type="submit" disabled={busy} className={AUTH_BUTTON_CLS} data-testid="reset-password-submit">
                            {busy ? "Updating…" : "Set new password"}
                        </button>
                    </form>
                    <div className="text-[10px] font-mono text-white/30 mt-4 text-center" data-testid="reset-password-expiry">
                        Link expires {(preview.expires_at || "").replace("T", " ").slice(0, 19)} UTC
                    </div>
                </>
            )}
        </AuthShell>
    );
}
